import { ReloadOutlined } from '@ant-design/icons';
import { Button, Descriptions, Tag, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTenantDateTime } from '../lib/tenant';

type Status = {
  version: string;
  database: 'ok' | 'unavailable';
  databaseDetails: {
    postgresVersion: string;
    databaseSize: string;
    connections: number;
    tableCount: number;
  } | null;
  attachments: { status: 'ok' | 'unavailable'; availableBytes: number | null };
  environment: {
    runtime: string;
    nodeVersion: string;
    platform: string;
    architecture: string;
    hostname: string;
    apiPort: number;
    uptimeSeconds: number;
  };
  timestamp: string;
};
export function SystemStatusPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<Status>();
  const [loading, setLoading] = useState(false);
  const [messageApi, holder] = message.useMessage();
  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/system/status', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      setData((await response.json()) as Status);
    } catch {
      await messageApi.error(t('system.loadFailed'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const uptime = (seconds?: number) => {
    if (seconds === undefined) return '-';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return days ? `${days}d ${hours}h ${minutes}m` : `${hours}h ${minutes}m`;
  };
  return (
    <section>
      {holder}
      <Button
        icon={<ReloadOutlined />}
        loading={loading}
        onClick={() => void load()}
        style={{ marginBottom: 16 }}
      >
        {t('system.refresh')}
      </Button>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('system.version')}>
          {data?.version ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.runtime')}>
          {data?.environment.runtime ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.nodeVersion')}>
          {data?.environment.nodeVersion ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.platform')}>
          {data?.environment.platform ?? '-'} /{' '}
          {data?.environment.architecture ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.hostname')}>
          {data?.environment.hostname ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.apiPort')}>
          {data?.environment.apiPort ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.uptime')}>
          {uptime(data?.environment.uptimeSeconds)}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.database')}>
          <Tag color={data?.database === 'ok' ? 'green' : 'red'}>
            {data?.database ?? '-'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('system.postgresVersion')}>
          {data?.databaseDetails?.postgresVersion ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.databaseSize')}>
          {data?.databaseDetails?.databaseSize ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.databaseConnections')}>
          {data?.databaseDetails?.connections ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.databaseTables')}>
          {data?.databaseDetails?.tableCount ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.attachments')}>
          <Tag color={data?.attachments.status === 'ok' ? 'green' : 'red'}>
            {data?.attachments.status ?? '-'}
          </Tag>
          {data?.attachments.availableBytes !== null &&
          data?.attachments.availableBytes !== undefined
            ? ` · ${(data.attachments.availableBytes / 1024 / 1024).toFixed(1)} MB`
            : ''}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.timestamp')}>
          {data ? formatTenantDateTime(data.timestamp) : '-'}
        </Descriptions.Item>
      </Descriptions>
    </section>
  );
}
